import type {
  WorkflowSkill,
  WorkflowSkillWithKind,
} from '../../../contracts/workflow-skill';

export function filterProjectsSkillSuggestions(
  skills: readonly WorkflowSkill[],
  query: string,
  limit: number
): WorkflowSkill[] {
  const q = query.trim().toLowerCase();
  const matched = q
    ? skills.filter(
        (skill) =>
          skill.slug.toLowerCase().includes(q) || skill.title.toLowerCase().includes(q)
      )
    : skills;
  return matched.slice(0, limit);
}

export type SpotlightWorkflowItem = {
  kind: 'workflow';
  skill: WorkflowSkill;
  label: string;
  slug: string;
};

export type SpotlightActionId = 'create-routine' | 'record';

export type SpotlightActionItem = {
  kind: 'action';
  /** Stable id for analytics tracking and picker keys. */
  actionId: SpotlightActionId;
  label: string;
  slug: string;
  /** Text the editor inserts when the action is picked. Caller-supplied so the
   * picker has no per-action dispatch — every action just inserts its prompt. */
  prompt: string;
};

/**
 * A runnable shortcut as the "/" menu needs it. The caller maps its own
 * catalogue into this shape and ranks it; this module only caps and lists.
 */
export type SpotlightSlashShortcut = {
  id: string;
  label: string;
  query: string;
  description?: string;
};

/** Evidence shortcut (saved pin or marketplace entry) runnable from "/". */
export type SpotlightEvidenceShortcutItem = {
  kind: 'evidence_shortcut';
  id: string;
  label: string;
  query: string;
  sub?: string;
};

export type SpotlightSlashItem =
  | SpotlightWorkflowItem
  | SpotlightActionItem
  | SpotlightEvidenceShortcutItem;

export type SpotlightSlashSectionId = 'actions' | 'shortcuts' | 'workflows';

type SpotlightSlashSectionOrder = 'routines-first' | 'shortcuts-first';

export interface SpotlightSlashSection {
  id: SpotlightSlashSectionId;
  items: readonly SpotlightSlashItem[];
}

const DEFAULT_SECTION_LIMIT = 5;

function filterActions(
  actions: readonly SpotlightActionItem[],
  query: string
): SpotlightActionItem[] {
  const q = query.trim().toLowerCase();
  return actions
    .filter((action) => !q || action.slug.includes(q))
    .slice(0, DEFAULT_SECTION_LIMIT);
}

function buildEvidenceShortcutSlashItems(
  filter: string,
  filterEvidenceShortcuts: (query: string) => readonly SpotlightSlashShortcut[]
): SpotlightEvidenceShortcutItem[] {
  return filterEvidenceShortcuts(filter)
    .slice(0, DEFAULT_SECTION_LIMIT)
    .map((shortcut) => ({
      kind: 'evidence_shortcut' as const,
      id: shortcut.id,
      label: shortcut.label,
      query: shortcut.query,
      sub: shortcut.description,
    }));
}

export function buildSpotlightSlashSections(
  query: string,
  sources: {
    skills: readonly WorkflowSkill[];
  },
  options?: {
    /** Caller-supplied slash actions (create-routine, record, …). The picker
     * only filters what it's given — it owns no action construction. */
    actions?: readonly SpotlightActionItem[];
    /** Evidence shortcuts matching the query, best match first. */
    filterEvidenceShortcuts?: (query: string) => readonly SpotlightSlashShortcut[];
    /** Actions always lead; this only swaps routines and shortcuts. */
    sectionOrder?: SpotlightSlashSectionOrder;
  }
): SpotlightSlashSection[] {
  const filter = query.trim();

  const workflows: SpotlightWorkflowItem[] = filterProjectsSkillSuggestions(
    sources.skills,
    filter,
    DEFAULT_SECTION_LIMIT
  ).map((skill) => ({
    kind: 'workflow' as const,
    skill,
    label: skill.title,
    slug: skill.slug,
  }));

  const actions = filterActions(options?.actions ?? [], filter);

  const shortcuts = options?.filterEvidenceShortcuts
    ? buildEvidenceShortcutSlashItems(filter, options.filterEvidenceShortcuts)
    : [];

  // `/` is scoped to actions + routines + shortcuts. Sessions, patients, and
  // library documents stay on `+` and `@`. Templates and forms are not in this menu.
  const workflowsSection = { id: 'workflows' as const, items: workflows };
  const shortcutsSection = { id: 'shortcuts' as const, items: shortcuts };
  return [
    { id: 'actions' as const, items: actions },
    ...(options?.sectionOrder === 'shortcuts-first'
      ? [shortcutsSection, workflowsSection]
      : [workflowsSection, shortcutsSection]),
  ].filter((section) => section.items.length > 0);
}

export function flattenSpotlightSections(
  sections: readonly SpotlightSlashSection[]
): SpotlightSlashItem[] {
  return sections.flatMap((section) => section.items);
}

export function isSpotlightWorkflowItem(
  item: SpotlightSlashItem
): item is SpotlightWorkflowItem {
  return item.kind === 'workflow';
}

export function isSpotlightActionItem(
  item: SpotlightSlashItem
): item is SpotlightActionItem {
  return item.kind === 'action';
}

export function isSpotlightEvidenceShortcutItem(
  item: SpotlightSlashItem
): item is SpotlightEvidenceShortcutItem {
  return item.kind === 'evidence_shortcut';
}

export function workflowSkillKind(
  skill: WorkflowSkill
): WorkflowSkillWithKind['kind'] | undefined {
  return (skill as WorkflowSkillWithKind).kind;
}
